import * as z from 'zod/v4'
import {getFieldCopy} from '@elanco/forms'
import type {LocaleKey, FieldKey} from '@elanco/forms'
import {logError} from '@/services/logger/client'
import {getCountryCodeFromLocale} from '@/utilities/locales'
import {ROLES} from '../data'
import type {FormField} from './model'

type RoleOption = (typeof ROLES)[number]

const getRoleCodenames = (roleSelections: unknown[] | undefined): string[] => {
	if (!Array.isArray(roleSelections)) {
		return []
	}

	const roleCodenames = roleSelections.flatMap((role) => {
		if (
			typeof role !== 'object' ||
			role === null ||
			typeof (role as {codename?: unknown}).codename !== 'string'
		) {
			return []
		}

		return [(role as {codename: string}).codename]
	})

	return [...new Set(roleCodenames)]
}

const getRoleOptionsByCodename = (roleCodenames: string[]): RoleOption[] => {
	const configuredRoleCodenames = new Set(roleCodenames)

	return ROLES.filter((role) => configuredRoleCodenames.has(role.id))
}

export const getConfiguredRoleOptions = (field: FormField): RoleOption[] => {
	const explicitRoleOptions = getRoleCodenames(
		field.elements?.formCdcRoleFieldOptions
	)

	if (explicitRoleOptions.length > 0) {
		return getRoleOptionsByCodename(explicitRoleOptions)
	}

	const configuredRoleCodenames = [
		...getRoleCodenames(
			field.elements?.formCdcRoleFieldRolesRequiringProfessionalNumber
		),
		...getRoleCodenames(field.elements?.formCdcRoleFieldOtherRoles),
	]

	if (configuredRoleCodenames.length === 0) {
		return ROLES
	}

	return getRoleOptionsByCodename(configuredRoleCodenames)
}

const getConfiguredRoleValues = (field: FormField): string[] => {
	return getConfiguredRoleOptions(field).map((role) => role.value)
}

export const getProfessionalNumberRoleValues = (field: FormField): string[] => {
	return getRoleOptionsByCodename(
		getRoleCodenames(
			field.elements?.formCdcRoleFieldRolesRequiringProfessionalNumber
		)
	).map((role) => role.value)
}

// Helper function to safely get field error messages
export const getFieldError = (
	locale: LocaleKey,
	field: FieldKey,
	errorType: string
): string | undefined => {
	const fieldCopy = getFieldCopy(locale, field)
	// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-explicit-any -- accessing dynamic error properties from field union type
	return (fieldCopy as any)?.errors?.[errorType]
}

// Type for form generation result
interface FormGenerationResult<T> {
	data: T
	error?: {
		message: string
		fieldType: string
		fieldCodename: string
		fieldId: string
	}
}

interface FieldSchemaHandlers {
	required: () => z.ZodTypeAny
	optional: () => z.ZodTypeAny
}

/**
 * Convert form field type to field name
 * @param type - The field type (e.g., 'form_select_field_name')
 * @returns The camelCase field name (e.g., 'fieldName')
 */
const convertTypeToFieldName = (type: string): string | undefined => {
	return FIELD_NAME_MAPPING[type]
}

// Field types that can be added to a form more than once - a shared type-based
// name would collide across instances, so these are suffixed with the field's
// unique codename.
const REPEATABLE_FIELD_TYPES = new Set([
	'form_checkbox_field',
	'form_checkbox_group_field',
	'form_radio_group_field',
	'form_select_field',
	'form_combobox_field',
	'form_text_field',
	'form_number_field',
])

/**
 * Derive the react-hook-form field name for a given form field. CDC fields map
 * to fixed profile property names (single instance per form). Repeatable
 * fields are suffixed with the field's unique codename to prevent collisions
 * when the same field type is used more than once on a form.
 */
export const getFormFieldName = (field: FormField): string => {
	if (
		field.system.type === 'form_cdc_email_field' &&
		field.elements?.formCdcEmailFieldRequireConfirmation?.[0]?.codename ===
			'yes'
	) {
		return 'confirmEmail'
	}

	const baseName = convertTypeToFieldName(field.system.type)

	if (!baseName) {
		return ''
	}

	if (REPEATABLE_FIELD_TYPES.has(field.system.type)) {
		return `${baseName}_${field.system.codename}`
	}

	return baseName
}

/**
 * Check if a form field is required based on its elements
 */
const isFieldRequired = (field: FormField): boolean => {
	if (!field.elements) return false

	return (
		// CDC Fields
		field.elements.formCdcFirstNameFieldRequired?.[0]?.codename === 'yes' ||
		field.elements.formCdcLastNameFieldRequired?.[0]?.codename === 'yes' ||
		field.elements.formCdcRoleFieldRequired?.[0]?.codename === 'yes' ||
		field.elements.formCdcBusinessSearchFieldRequired?.[0]?.codename ===
			'yes' ||
		field.elements.formCdcSalutationFieldRequired?.[0]?.codename ===
			'yes' ||
		field.elements.formCdcTelephoneFieldRequired?.[0]?.codename === 'yes' ||
		field.elements.formCdcBusinessTelephoneFieldRequired?.[0]?.codename ===
			'yes' ||
		field.elements.formCdcBusinessNameFieldRequired?.[0]?.codename ===
			'yes' ||
		field.elements.formCdcProfessionalNumberFieldRequired?.[0]?.codename ===
			'yes' ||
		field.elements.formCdcBusinessTypeFieldRequired?.[0]?.codename ===
			'yes' ||
		field.elements.formCdcExplicitConsentCheckboxFieldRequired?.[0]
			?.codename === 'yes' ||
		field.elements.formCdcExplicitSmsConsentCheckboxFieldRequired?.[0]
			?.codename === 'yes' ||
		// Non-CDC Fields
		field.elements.formCheckboxFieldRequired?.[0]?.codename === 'yes' ||
		field.elements.formCheckboxGroupFieldRequired?.[0]?.codename ===
			'yes' ||
		field.elements.formComboboxFieldRequired?.[0]?.codename === 'yes' ||
		field.elements.formRadioGroupFieldRequired?.[0]?.codename === 'yes' ||
		field.elements.formSelectFieldRequired?.[0]?.codename === 'yes' ||
		field.elements.formTextFieldRequired?.[0]?.codename === 'yes' ||
		field.elements.formNumberFieldRequired?.[0]?.codename === 'yes' ||
		field.elements.formTelephoneFieldRequired?.[0]?.codename === 'yes' ||
		field.elements.formSwitchFieldRequired?.[0]?.codename === 'yes'
	)
}

/**
 * Create schema for CDC email field
 * Note: Email field is always required by design (no CMS option to make it optional)
 */
const createEmailFieldSchema = (locale: LocaleKey): z.ZodTypeAny => {
	return z
		.string()
		.min(1, getFieldError(locale, 'email', 'required'))
		.email(getFieldError(locale, 'email', 'invalidEmail'))
}

/**
 * Create schema for CDC first name field
 */
const createFirstNameFieldSchema = (
	locale: LocaleKey,
	isRequired: boolean
): z.ZodTypeAny => {
	return z
		.string()
		.max(50, getFieldError(locale, 'firstName', 'maxLength'))
		.refine((val) => !isRequired || val.length >= 1, {
			message: getFieldError(locale, 'firstName', 'required'),
		})
		.refine((val) => val.length === 0 || val.length >= 2, {
			message: getFieldError(locale, 'firstName', 'minLength'),
		})
}

/**
 * Create schema for CDC last name field
 */
const createLastNameFieldSchema = (
	locale: LocaleKey,
	isRequired: boolean
): z.ZodTypeAny => {
	return z
		.string()
		.max(50, getFieldError(locale, 'lastName', 'maxLength'))
		.refine((val) => !isRequired || val.length >= 1, {
			message: getFieldError(locale, 'lastName', 'required'),
		})
		.refine((val) => val.length === 0 || val.length >= 2, {
			message: getFieldError(locale, 'lastName', 'minLength'),
		})
}

/**
 * Create schema for CDC business name field
 */
const createBusinessNameFieldSchema = (
	locale: LocaleKey,
	isRequired: boolean
): z.ZodTypeAny => {
	return z
		.string()
		.max(100, getFieldError(locale, 'businessName', 'maxLength'))
		.refine((val) => !isRequired || val.length >= 1, {
			message: getFieldError(locale, 'businessName', 'required'),
		})
		.refine((val) => val.length === 0 || val.length >= 2, {
			message: getFieldError(locale, 'businessName', 'minLength'),
		})
}

/**
 * Create schema for CDC role field
 */
const createRoleFieldSchema = (
	field: FormField,
	locale: LocaleKey,
	isRequired: boolean
): z.ZodTypeAny => {
	const allowedRoleValues = getConfiguredRoleValues(field)

	return z
		.string()
		.refine(
			(val) =>
				(val.length === 0 && !isRequired) ||
				allowedRoleValues.includes(val),
			{
				message: getFieldError(locale, 'role', 'invalid'),
			}
		)
}

/**
 * Create schema for CDC business search field
 */
const createBusinessSearchFieldSchema = (
	locale: LocaleKey,
	isRequired: boolean
): z.ZodTypeAny => {
	if (isRequired) {
		return z.object({
			_validation: z
				.string()
				.min(
					1,
					getFieldError(locale, 'businessSearchField', 'required')
				),
			entryMethod: z.string().optional(),
			business: z.object({
				businessType: z
					.string()
					.min(1, getFieldError(locale, 'businessType', 'required')),
				name: z
					.string()
					.min(1, getFieldError(locale, 'businessName', 'required')),
				line1: z
					.string()
					.min(1, getFieldError(locale, 'addressLine1', 'required')),
				line2: z.string().optional(),
				city: z
					.string()
					.min(1, getFieldError(locale, 'city', 'required')),
				state: z
					.string()
					.min(1, getFieldError(locale, 'state', 'required')),
				postalCode: z
					.string()
					.min(1, getFieldError(locale, 'zipCode', 'required')),
				CRMId: z.string().optional(),
			}),
		})
	}

	return z.object({
		_validation: z.string().optional(),
		entryMethod: z.string().optional(),
		business: z
			.object({
				businessType: z.string().optional(),
				name: z.string().optional(),
				line1: z.string().optional(),
				line2: z.string().optional(),
				city: z.string().optional(),
				state: z.string().optional(),
				postalCode: z.string().optional(),
				CRMId: z.string().optional(),
			})
			.optional(),
	})
}

/**
 * Create schema for CDC explicit consent field
 */
const createExplicitConsentFieldSchema = (
	locale: LocaleKey,
	isRequired: boolean
): z.ZodTypeAny => {
	return isRequired
		? z.boolean().refine((val) => val, {
				message: getFieldError(locale, 'explicitConsent', 'required'),
			})
		: z.boolean()
}

/**
 * Create schema for CDC explicit SMS consent field
 */
const createExplicitSmsConsentFieldSchema = (
	locale: LocaleKey,
	isRequired: boolean
): z.ZodTypeAny => {
	return isRequired
		? z.boolean().refine((val) => val, {
				message: getFieldError(
					locale,
					'explicitSmsConsent',
					'required'
				),
			})
		: z.boolean()
}

/**
 * Create schema for CDC implicit consent field
 */
const createImplicitConsentFieldSchema = (): z.ZodTypeAny => {
	return z.literal(true)
}

/**
 * Create required schema for CDC salutation field
 */
const createRequiredSalutationFieldSchema = (
	locale: LocaleKey
): z.ZodTypeAny => {
	return z.string().min(1, getFieldError(locale, 'salutation', 'required'))
}

/**
 * Create optional schema for CDC salutation field
 */
const createOptionalSalutationFieldSchema = (): z.ZodTypeAny => {
	return z.string().optional()
}

/**
 * Create schema for CDC telephone field
 */
const createTelephoneFieldSchema = (
	locale: LocaleKey,
	isRequired: boolean
): z.ZodTypeAny => {
	const baseSchema = z.object({
		diallingCode: z.string(),
		localNumber: z.string(),
		country: z.string().optional(),
	})

	if (isRequired) {
		return baseSchema
			.refine(
				(data) => {
					const hasDiallingCode =
						data.diallingCode && data.diallingCode.length > 0
					const hasLocalNumber =
						data.localNumber && data.localNumber.length > 0
					return hasDiallingCode && hasLocalNumber
				},
				{
					message: getFieldError(
						locale,
						'telephoneField',
						'invalidTelephone'
					),
					path: [],
				}
			)
			.refine(
				(data) => {
					if (data.localNumber && data.localNumber.length > 0) {
						return /^\d+$/.test(data.localNumber)
					}
					return true
				},
				{
					message: getFieldError(
						locale,
						'telephoneField',
						'invalidFormat'
					),
					path: [],
				}
			)
	}

	return baseSchema.refine(
		(data) => {
			if (!data.localNumber || data.localNumber.length === 0) {
				return true
			}
			return /^\d+$/.test(data.localNumber)
		},
		{
			message: getFieldError(locale, 'telephoneField', 'invalidFormat'),
			path: [],
		}
	)
}

/**
 * Create schema for text field
 */
const createTextFieldSchema = (
	locale: LocaleKey,
	isRequired: boolean
): z.ZodTypeAny => {
	return z
		.string()
		.max(255, getFieldError(locale, 'text', 'maxLength'))
		.refine((val) => !isRequired || val.length >= 1, {
			message: getFieldError(locale, 'text', 'required'),
		})
		.refine((val) => val.length === 0 || val.length >= 2, {
			message: getFieldError(locale, 'text', 'minLength'),
		})
}

/**
 * Create schema for combobox field
 */
const createComboboxFieldSchema = (
	locale: LocaleKey,
	isRequired: boolean
): z.ZodTypeAny => {
	return z.string().refine((val) => !isRequired || val.length >= 1, {
		message: getFieldError(locale, 'combobox', 'required'),
	})
}

/**
 * Create schema for number field
 */
const createNumberFieldSchema = (
	field: FormField,
	locale: LocaleKey,
	isRequired: boolean
): z.ZodTypeAny => {
	return z
		.number()
		.nullable()
		.refine((val) => !isRequired || val !== null, {
			message: getFieldError(locale, 'number', 'required'),
		})
		.refine(
			(val) =>
				val === null ||
				(field.elements?.formNumberFieldMin
					? val >= field.elements.formNumberFieldMin
					: true),
			{message: getFieldError(locale, 'number', 'minValue')}
		)
		.refine(
			(val) =>
				val === null ||
				(field.elements?.formNumberFieldMax
					? val <= field.elements.formNumberFieldMax
					: true),
			{message: getFieldError(locale, 'number', 'maxValue')}
		)
}

/**
 * Create schema for checkbox group field
 */
const createCheckboxGroupFieldSchema = (
	locale: LocaleKey,
	isRequired: boolean
): z.ZodTypeAny => {
	return z.array(z.string()).refine((val) => !isRequired || val.length >= 1, {
		message: getFieldError(locale, 'checkboxGroup', 'required'),
	})
}

/**
 * Create schema for radio group field
 */
const createRadioGroupFieldSchema = (
	locale: LocaleKey,
	isRequired: boolean
): z.ZodTypeAny => {
	return z.string().refine((val) => !isRequired || val.length >= 1, {
		message: getFieldError(locale, 'checkboxGroup', 'required'),
	})
}

/**
 * Create schema for select field
 */
const createSelectFieldSchema = (
	locale: LocaleKey,
	isRequired: boolean
): z.ZodTypeAny => {
	return z.string().refine((val) => !isRequired || val.length >= 1, {
		message: getFieldError(locale, 'select', 'required'),
	})
}

/**
 * Create required schema for checkbox field
 */
const createRequiredCheckboxFieldSchema = (locale: LocaleKey): z.ZodTypeAny => {
	return z.boolean().refine((val) => val, {
		message: getFieldError(locale, 'checkbox', 'required'),
	})
}

/**
 * Create optional schema for checkbox field
 */
const createOptionalCheckboxFieldSchema = (): z.ZodTypeAny => {
	return z.boolean()
}

/**
 * Get field schema based on field type
 */
const getFieldSchemaHandlers = (
	field: FormField,
	locale: LocaleKey
): FieldSchemaHandlers | undefined => {
	const fieldType = field.system.type

	switch (fieldType) {
		case 'form_cdc_email_field': {
			if (
				field.elements?.formCdcEmailFieldRequireConfirmation?.[0]
					?.codename === 'yes'
			) {
				return {
					required: () => z.string(),
					optional: () => z.string(),
				}
			}
			return {
				required: () => createEmailFieldSchema(locale),
				optional: () => createEmailFieldSchema(locale),
			}
		}
		case 'form_cdc_first_name_field':
			return {
				required: () => createFirstNameFieldSchema(locale, true),
				optional: () => createFirstNameFieldSchema(locale, false),
			}
		case 'form_cdc_last_name_field':
			return {
				required: () => createLastNameFieldSchema(locale, true),
				optional: () => createLastNameFieldSchema(locale, false),
			}
		case 'form_cdc_business_name_field':
			return {
				required: () => createBusinessNameFieldSchema(locale, true),
				optional: () => createBusinessNameFieldSchema(locale, false),
			}
		case 'form_cdc_role_field':
			return {
				required: () => createRoleFieldSchema(field, locale, true),
				optional: () => createRoleFieldSchema(field, locale, false),
			}
		case 'form_cdc_business_search_field':
			return {
				required: () => createBusinessSearchFieldSchema(locale, true),
				optional: () => createBusinessSearchFieldSchema(locale, false),
			}
		case 'form_cdc_explicit_consent_field':
			return {
				required: () => createExplicitConsentFieldSchema(locale, true),
				optional: () => createExplicitConsentFieldSchema(locale, false),
			}
		case 'form_cdc_explicit_sms_consent_field':
			return {
				required: () =>
					createExplicitSmsConsentFieldSchema(locale, true),
				optional: () =>
					createExplicitSmsConsentFieldSchema(locale, false),
			}
		case 'form_cdc_implicit_consent_field':
			return {
				required: () => createImplicitConsentFieldSchema(),
				optional: () => createImplicitConsentFieldSchema(),
			}
		case 'form_cdc_salutation_field':
			return {
				required: () => createRequiredSalutationFieldSchema(locale),
				optional: () => createOptionalSalutationFieldSchema(),
			}
		case 'form_cdc_telephone_field':
		case 'form_cdc_business_telephone_field':
			return {
				required: () => createTelephoneFieldSchema(locale, true),
				optional: () => createTelephoneFieldSchema(locale, false),
			}
		case 'form_checkbox_group_field':
			return {
				required: () => createCheckboxGroupFieldSchema(locale, true),
				optional: () => createCheckboxGroupFieldSchema(locale, false),
			}
		case 'form_radio_group_field':
			return {
				required: () => createRadioGroupFieldSchema(locale, true),
				optional: () => createRadioGroupFieldSchema(locale, false),
			}
		case 'form_select_field':
			return {
				required: () => createSelectFieldSchema(locale, true),
				optional: () => createSelectFieldSchema(locale, false),
			}
		case 'form_combobox_field':
			return {
				required: () => createComboboxFieldSchema(locale, true),
				optional: () => createComboboxFieldSchema(locale, false),
			}
		case 'form_checkbox_field':
			return {
				required: () => createRequiredCheckboxFieldSchema(locale),
				optional: () => createOptionalCheckboxFieldSchema(),
			}
		case 'form_text_field':
			return {
				required: () => createTextFieldSchema(locale, true),
				optional: () => createTextFieldSchema(locale, false),
			}
		case 'form_number_field':
			return {
				required: () => createNumberFieldSchema(field, locale, true),
				optional: () => createNumberFieldSchema(field, locale, false),
			}
		default:
			return undefined
	}
}

const shouldSkipFieldForSchema = (
	fieldType: string,
	fieldName: string
): boolean => {
	return (
		!fieldName ||
		fieldType === 'form_button' ||
		fieldType === 'form_display_text'
	)
}

const getFieldSchemaResult = (
	field: FormField,
	locale: LocaleKey
): FormGenerationResult<z.ZodTypeAny> => {
	const schemaHandlers = getFieldSchemaHandlers(field, locale)

	if (!schemaHandlers) {
		const schemaError = new Error(
			`Unsupported field type in form schema: ${field.system.type}. Field codename: ${field.system.name}. Field ID: ${field.system.id}`
		)
		logError(schemaError)

		return {
			data: z.object({}),
			error: {
				message: 'Unsupported field',
				fieldType: field.system.type,
				fieldCodename: field.system.name,
				fieldId: field.system.id,
			},
		}
	}

	const fieldSchema = isFieldRequired(field)
		? schemaHandlers.required()
		: schemaHandlers.optional()

	return {data: fieldSchema}
}

const maybeAddProfessionalNumberSchema = (
	schemaObj: Record<string, z.ZodTypeAny>,
	field: FormField,
	locale: LocaleKey
): void => {
	if (
		field.system.type !== 'form_cdc_role_field' ||
		(field.elements?.formCdcRoleFieldRolesRequiringProfessionalNumber
			?.length ?? 0) === 0 ||
		'professionalNumber' in schemaObj
	) {
		return
	}

	schemaObj.professionalNumber = z
		.object({
			number: z
				.string()
				.min(
					1,
					getFieldError(locale, 'professionalNumber', 'required')
				),
			state: z
				.string()
				.min(1, getFieldError(locale, 'jurisdiction', 'required')),
		})
		.optional()
}

const addEmailConfirmationValidation = (
	schemaObj: Record<string, z.ZodTypeAny>,
	locale: LocaleKey
): z.ZodObject<Record<string, z.ZodTypeAny>> => {
	const formSchema = z.object(schemaObj)

	if (!('email' in schemaObj) || !('confirmEmail' in schemaObj)) {
		return formSchema
	}

	return formSchema.refine(
		(data) => {
			return data.email === data.confirmEmail
		},
		{
			message: getFieldError(locale, 'confirmEmail', 'emailMatchError'),
			path: ['confirmEmail'],
		}
	) as z.ZodObject<Record<string, z.ZodTypeAny>>
}

/**
 * Generate Zod schema dynamically based on form questions
 */
export const generateFormSchema = (
	questions: FormField[],
	locale: LocaleKey
): FormGenerationResult<z.ZodObject<Record<string, z.ZodTypeAny>>> => {
	const schemaObj: Record<string, z.ZodTypeAny> = {}

	// Handle empty questions array
	if (!Array.isArray(questions) || questions.length === 0) {
		return {
			data: z.object({}),
		}
	}

	for (const field of questions) {
		// Skip invalid field objects
		if (!field.system.type || !field.system.codename) {
			continue
		}

		const fieldType = field.system.type
		const fieldName = getFormFieldName(field)

		// Skip buttons and display text as they don't need schema validation
		if (shouldSkipFieldForSchema(fieldType, fieldName)) {
			continue
		}

		const fieldSchemaResult = getFieldSchemaResult(field, locale)

		if (fieldSchemaResult.error) {
			return {
				data: z.object({}),
				error: fieldSchemaResult.error,
			}
		}

		schemaObj[fieldName] = fieldSchemaResult.data
		maybeAddProfessionalNumberSchema(schemaObj, field, locale)
	}

	return {
		data: addEmailConfirmationValidation(schemaObj, locale),
	}
}

/**
 * Generate default values for the form based on questions
 */
export const generateDefaultValues = (
	questions: FormField[]
): FormGenerationResult<
	Record<string, string | string[] | boolean | object | null>
> => {
	const defaults: Record<
		string,
		string | string[] | boolean | object | null
	> = {}

	if (questions.length === 0) {
		return {
			data: defaults,
		}
	}

	for (const field of questions) {
		// Skip empty or malformed fields
		if (!field.system.type || !field.system.codename) {
			continue
		}
		const fieldType = field.system.type
		const origFieldName = field.system.name

		const fieldName = getFormFieldName(field)

		// Skip fields without a valid name mapping
		if (!fieldName) {
			continue
		}

		// Set default values based on field type
		switch (fieldType) {
			case 'form_cdc_email_field':
			case 'form_cdc_first_name_field':
			case 'form_cdc_last_name_field':
			case 'form_cdc_role_field':
			case 'form_cdc_business_name_field':
			case 'form_cdc_salutation_field':
			case 'form_radio_group_field':
			case 'form_select_field':
			case 'form_combobox_field':
			case 'form_text_field':
				defaults[fieldName] = ''
				break
			case 'form_cdc_business_search_field':
				defaults[fieldName] = {
					_validation: '',
					entryMethod: 'automatic',
					business: {
						businessType: '',
						name: '',
						line1: '',
						line2: '',
						city: '',
						state: '',
						postalCode: '',
						CRMId: '',
					},
				}
				break
			case 'form_cdc_explicit_consent_field':
			case 'form_cdc_explicit_sms_consent_field':
			case 'form_checkbox_field':
				defaults[fieldName] = false
				break
			case 'form_cdc_implicit_consent_field':
				defaults[fieldName] = true
				break
			case 'form_cdc_telephone_field':
			case 'form_cdc_business_telephone_field':
				defaults[fieldName] = {
					diallingCode: '',
					localNumber: '',
				}
				break
			case 'form_checkbox_group_field':
				defaults[fieldName] = []
				break
			case 'form_number_field':
				defaults[fieldName] = null
				break
			case 'form_button':
			case 'form_display_text':
				break
			default: {
				// Log error for monitoring and Azure alerting
				const defaultsError = new Error(
					`Unsupported field type in default values: ${fieldType}. Field codename: ${origFieldName}. Field ID: ${field.system.id}`
				)
				logError(defaultsError)
				// Return error instead of throwing
				return {
					data: {},
					error: {
						message: 'Unsupported field',
						fieldType,
						fieldCodename: origFieldName,
						fieldId: field.system.id,
					},
				}
			}
		}

		// Auto-inject professionalNumber default when role field has roles requiring it
		if (
			fieldType === 'form_cdc_role_field' &&
			(field.elements?.formCdcRoleFieldRolesRequiringProfessionalNumber
				?.length ?? 0) > 0 &&
			!('professionalNumber' in defaults)
		) {
			defaults.professionalNumber = {
				number: '',
				state: '',
			}
		}
	}

	return {
		data: defaults,
	}
}

const FIELD_NAME_MAPPING: Record<string, string> = {
	form_cdc_first_name_field: 'firstName',
	form_cdc_last_name_field: 'lastName',
	form_cdc_email_field: 'email',
	form_cdc_role_field: 'role',
	form_cdc_business_search_field: 'businessSearch',
	form_cdc_salutation_field: 'salutation',
	form_cdc_telephone_field: 'telephone',
	form_cdc_business_telephone_field: 'businessTelephone',
	form_cdc_business_name_field: 'businessName',
	form_cdc_explicit_consent_field: 'explicitConsent',
	form_cdc_explicit_sms_consent_field: 'smsConsent',
	form_cdc_implicit_consent_field: 'implicitConsent',
	form_cdc_address_city_field: 'addressCity',
	form_cdc_address_line1_field: 'addressLine1',
	form_cdc_address_line2_field: 'addressLine2',
	form_cdc_address_zip_code_field: 'addressPostalCode',
	form_cdc_address_state_field: 'addressState',
	form_checkbox_group_field: 'checkboxGroup',
	form_radio_group_field: 'radioGroup',
	form_select_field: 'select',
	form_checkbox_field: 'checkbox',
	form_combobox_field: 'combobox',
	form_text_field: 'text',
	form_number_field: 'number',
	form_address_search_field: 'addressSearch',
	form_display_text: 'displayText',
	form_button: 'button',
}

export const extractCountryCode = (code: string): string => {
	return getCountryCodeFromLocale(code)
}
